import { showError, showSuccess } from '@calendly/alert'
import { I18n } from '@calendly/i18n'
import { Segment } from '@calendly/segment'

import { trackEvent } from 'hooks/analytics_events'
import { rootApi } from 'root-api'
import { uuidSelector } from 'selectors/current_user'

import { trackContactUpdatedEvent } from './analytics'
import { isContactDrawerOpenSelector } from './selectors'
import { setCurrentContactProfile } from './slice'

const api = rootApi.enhanceEndpoints({ addTagTypes: ['CONTACTS', 'CONTACT', 'CONTACTS-PARTIAL-LIST', 'CONTACTS-SEARCH'] }).injectEndpoints({
  endpoints: (build) => ({
    fetchContacts: build.query({
      query: ({ favorite = false, pagePath }) => ({
        url: '/api/dashboard/contacts/',
        params: { contact: { favorite, page_path: pagePath } }
      }),
      transformResponse: (baseQueryReturnValue) => {
        baseQueryReturnValue.contacts.forEach((contact) => {
          contact.nextEvent = contact.nextEvent[0]
          contact.lastEvent = contact.lastEvent[0]
        })
        return baseQueryReturnValue
      },
      providesTags: (result) => (
        result?.contacts
          ? [...result.contacts.map(({ email }) => ({ type: 'CONTACTS', id: email })), {
            type: 'CONTACTS',
            id: 'CONTACTS-PARTIAL-LIST'
          }]
          : [{ type: 'CONTACTS', id: 'CONTACTS-PARTIAL-LIST' }]
      )
    }),
    searchContacts: build.query({
      query: ({ query, withEvents }) => ({
        url: '/api/dashboard/contacts/search',
        params: { query, withEvents }
      }),
      transformResponse: (baseQueryReturnValue, _meta, arg) => {
        if (!arg.withEvents) return baseQueryReturnValue

        baseQueryReturnValue.contacts.forEach((contact) => {
          contact.nextEvent = contact.nextEvent[0]
          contact.lastEvent = contact.lastEvent[0]
        })

        return baseQueryReturnValue
      },
      providesTags: ['CONTACTS-SEARCH']
    }),
    toggleContactFavorite: build.mutation({
      query: (data) => ({
        url: `/api/dashboard/contacts/${data.contact.uuid}`,
        method: 'PATCH',
        params: data,
      }),
      invalidatesTags: (result) => [
        { type: 'CONTACTS', id: result.email },
        { type: 'CONTACTS', id: 'CONTACTS-PARTIAL-LIST' },
        'CONTACT',
        'EMPTY_STATES_ACTIVATION_DATA'
      ],
      extraOptions: {
        onSuccess: (contact, { dispatch, getState }) => {
          const isContactDrawerOpen = isContactDrawerOpenSelector(getState())

          if (contact.favorite) {
            Segment.track('contact_management.user.favorited_contact', { contact_uuid: contact.uuid })
          }
          const userUuid = uuidSelector(getState())
          const eventTrackingData = {
            contactUuid: contact.uuid,
            contactSource: null,
            updateType: contact.favorite ? 'favorite' : 'remove_favorite'
          }
          trackContactUpdatedEvent(eventTrackingData, (config) => {
            trackEvent({
              ...config,
              dependencies: { user_uuid: userUuid }
            })
          })
          dispatch(showSuccess('Contact updated'))
          if (isContactDrawerOpen) {
            dispatch(setCurrentContactProfile(contact))
          }
        }
      }
    }),
    addContact: build.mutation({
      query: (data) => ({
        url: '/api/dashboard/contacts',
        method: 'POST',
        data
      }),
      extraOptions: {
        onSuccess: (_, { dispatch }) => {
          dispatch(showSuccess('Contact created'))
        }
      },
      invalidatesTags: (result) => [
        { type: 'CONTACTS', id: result.email },
        { type: 'CONTACTS', id: 'CONTACTS-PARTIAL-LIST' },
        'EMPTY_STATES_ACTIVATION_DATA'
      ]
    }),
    removeContact: build.mutation({
      query: (data) => ({
        url: `/api/dashboard/contacts/${data.contact.uuid}`,
        method: 'PUT',
        params: data
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled
        } catch (e) {
          const error = e.error
          if (error.payload && error.payload.body) {
            const parsedError = JSON.parse(error.payload.body)
            dispatch(showError(parsedError.message))
          }
        }
      },
      extraOptions: {
        onSuccess: (response, { dispatch, getState }) => {
          const userUuid = uuidSelector(getState())
          const eventTrackingData = {
            contactUuid: response.uuid,
            contactSource: 'contact_list',
            updateType: 'remove'
          }

          trackContactUpdatedEvent(eventTrackingData, (config) => {
            trackEvent({
              ...config,
              dependencies: { user_uuid: userUuid }
            })
          })
          dispatch(showSuccess('Contact removed'))
        }
      },
      invalidatesTags: () => [
        { type: 'CONTACTS' },
        { type: 'CONTACTS', id: 'CONTACTS-PARTIAL-LIST' },
        'CONTACTS-SEARCH',
        'EMPTY_STATES_ACTIVATION_DATA'
      ]
    }),
    restoreContact: build.mutation({
      query: (data) => ({
        url: `/api/dashboard/contacts/${data.contact.uuid}`,
        method: 'PUT',
        params: data
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled
        } catch (e) {
          const error = e.error
          if (error.payload && error.payload.body) {
            const parsedError = JSON.parse(error.payload.body)
            dispatch(showError(parsedError.message))
          }
        }
      },
      extraOptions: {
        onSuccess: (_, { dispatch }) => {
          dispatch(showSuccess(I18n.t('publisher.contacts.flash.contact_restored')))
        }
      },
      invalidatesTags: () => [
        { type: 'CONTACTS' },
        { type: 'CONTACTS', id: 'CONTACTS-PARTIAL-LIST' },
        'CONTACTS-SEARCH',
        'EMPTY_STATES_ACTIVATION_DATA'
      ]
    }),
    fetchContactByEmail: build.query({
      query: (email) => ({
        url: '/api/dashboard/contacts',
        params: { contact: { email } }
      }),
      extraOptions: {
        handleErrors: false
      },
      transformResponse(baseQueryReturnValue) {
        return baseQueryReturnValue?.contacts?.[0]
      },
      providesTags: ['CONTACT']
    }),
    editContact: build.mutation({
      query: (data) => ({
        url: `/api/dashboard/contacts/${data.contact.uuid}`,
        method: 'PUT',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled
        } catch (e) {
          const error = e.error
          if (error.payload && error.payload.body) {
            const parsedError = JSON.parse(error.payload.body)
            dispatch(showError(parsedError.message))
          }
        }
      },
      extraOptions: {
        onSuccess: (response, { dispatch, getState }) => {
          const userUuid = uuidSelector(getState())
          const eventTrackingData = {
            contactUuid: response.uuid,
            contactSource: null,
            updateType: 'edit'
          }
          trackContactUpdatedEvent(eventTrackingData, (config) => {
            trackEvent({
              ...config,
              dependencies: { user_uuid: userUuid }
            })
          })
          dispatch(showSuccess('Contact updated'))
          dispatch(setCurrentContactProfile(response))
        }
      },
      invalidatesTags: (result) => [
        { type: 'CONTACTS', id: result.email },
        { type: 'CONTACTS', id: 'CONTACTS-PARTIAL-LIST' }
      ]
    }),
    fetchContact: build.query({
      query: ({ uuid, email }) => ({
        url: `/api/dashboard/contacts/${uuid}`,
        params: { email }
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled
        } catch (e) {
          const error = e.error
          if (error.payload && error.payload.message) {
            dispatch(showError(error.payload.message))
          }
        }
      },
      providesTags: ['CONTACT']
    })
  })
})

export const {
  useFetchContactsQuery,
  useAddContactMutation,
  useRemoveContactMutation,
  useRestoreContactMutation,
  useToggleContactFavoriteMutation,
  useFetchContactByEmailQuery,
  useEditContactMutation,
  useSearchContactsQuery,
  useFetchContactQuery,
  util: contactsApiUtil,
} = api
